import React from 'react';
import moment from 'moment';
import _ from 'lodash';

import Button from 'react-bootstrap/lib/Button';
import Label from 'components/Label/Label';
import Panel from 'components/Panel/Panel';
import Price from 'components/Price/Price';

import {
  MapMarkerIcon,
  WorldGlobeIcon,
  ChevronRightIcon,
} from './BriefListingIcons';
import styles from './BriefListingCard.module.scss';
import { Link } from 'react-router-dom';
import { LinkContainer } from 'react-router-bootstrap';
import {
  FREE_USER_JOB_PITCH_LIMIT,
  VERY_POPULAR_PITCH_COUNT,
} from 'constants/constants';

const BriefListingCard = ({ brief = null, onClickRole = null }) => {
  const renderBriefPopularityBadge = (brief) => {
    if (brief.num_of_pitches <= FREE_USER_JOB_PITCH_LIMIT) {
      return (
        <span title="Pitch before other freelancers">
          <Label className={styles.lowPopularityBadgeLabel}>Open job</Label>
        </span>
      );
    }

    return (
      <span title="Pitch soon – this client has received pitches">
        <Label className={styles.popularityBadgeLabel}>
          {`${
            brief.num_of_pitches >= VERY_POPULAR_PITCH_COUNT ? 'Very ' : ''
          }Popular job`}
        </Label>
      </span>
    );
  };

  const renderBriefTitle = (brief) => {
    return (
      <div
        title={`${brief.text} – ${
          brief.remote ? 'Remote Job' : brief.location
        }`}
        className={styles.briefTitleContainer}
      >
        <div className={styles.briefTitle}>
          {brief.text}
          {!brief.remote ? (
            <MapMarkerIcon size="25" style={{ verticalAlign: 'sub' }} />
          ) : (
            <WorldGlobeIcon size="25" style={{ verticalAlign: 'sub' }} />
          )}
          {!brief.remote && brief.location}
        </div>

        <div className={styles.briefBudgetStatusContainer}>
          {brief.status === 'active' && (
            <div className={styles.briefPopularityBadge}>
              {renderBriefPopularityBadge(brief)}
            </div>
          )}
        </div>
      </div>
    );
  };

  const renderBriefSpec = (brief) => {
    const { spec } = brief;
    let newSpec = spec;

    // Remove all occurrences of `**`
    newSpec = _.replace(newSpec, /\*\*/g, '');

    // Truncate description as not displayed beyond 2 lines
    return newSpec.substring(0, 300);
  };

  return (
    <Panel className={styles.container}>
      <div>
        {/* Gray Placeholder - used when loading Briefs from API */}
        {!brief && (
          <div className={styles.emptyTop}>
            <div className={styles.emptyAvatar} />
            <div className={styles.emptyTitle} />
          </div>
        )}

        {brief && (
          <div>
            {brief.status === 'active' ? (
              <Link className={styles.title} to={brief.links.main_relative}>
                {renderBriefTitle(brief)}
              </Link>
            ) : (
              <span className={styles.title}>{renderBriefTitle(brief)}</span>
            )}
          </div>
        )}
      </div>

      {/* Brief Subtitle - RoleSkill & Budget Pills */}
      <div className={styles.labels}>
        {!brief && <div className={styles.emptyLabels} />}

        {brief && (
          <div>
            <Label
              className={styles.roleSkillPillButton}
              color="primary"
              inverse
              onClick={() => onClickRole({ name: brief.role || brief.skill })}
            >
              {brief.role || brief.skill}
            </Label>

            {brief.amount && brief.amount !== 0 && (
              <Label
                className={styles.budgetPillButton}
                color="success"
                inverse
              >
                <span>Budget </span>
                <Price currency={brief.currency} amount={brief.budget} />
              </Label>
            )}
          </div>
        )}
      </div>

      {/* Brief Description */}
      <div className={styles.description}>
        {!brief && (
          <div className={styles.emptyDescription}>
            <div />
            <div />
          </div>
        )}
        {brief && renderBriefSpec(brief)}
      </div>

      {/* Posting & Expiration Dates */}
      <div className={styles.dates}>
        {!brief && <div className={styles.emptyDates} />}
        {brief && (
          <div>
            <span>Posted {moment(brief.approved_at).fromNow()}</span>
            <span className={styles.bullet}>&bull;</span>
            <span>Expires in {moment(brief.expires_at).fromNow(true)}</span>
          </div>
        )}
      </div>

      {/* CTA (i.e. More Info button) */}
      <div className={styles.ctas}>
        {!brief && <div className={styles.emptyCta} />}
        {brief && (
          <div>
            {brief.status === 'active' && (
              <LinkContainer to={brief.links.main_relative}>
                <Button
                  block
                  bsStyle="success"
                  className={styles.moreInfoButton}
                >
                  More info <ChevronRightIcon />
                </Button>
              </LinkContainer>
            )}
            {(brief.status === 'in_progress' ||
              brief.status === 'paid' ||
              brief.status === 'final_files_uploaded' ||
              brief.status === 'paid_final_files_uploaded' ||
              brief.status === 'final_files_approved' ||
              brief.status === 'paid_final_files_approved') && (
              <div className={`${styles.statusLabel} ${styles.inProgress}`}>
                In progress
              </div>
            )}
            {brief.status === 'completed' && (
              <div className={`${styles.statusLabel} ${styles.completed}`}>
                Recently completed
              </div>
            )}
            {(brief.status === 'expired' || brief.status === 'inactive') && (
              <div className={`${styles.statusLabel} ${styles.complete}`}>
                Inactive
              </div>
            )}
          </div>
        )}
      </div>
    </Panel>
  );
};

export default BriefListingCard;
